import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import IconButton from './IconButton';
import { useActiveTab } from '../context/TabContext';
import './comic.css';

/**
 * Shared comic-panel-styled popup, used wherever a flow that used to expand
 * inline now opens as a dialog instead (per direct user request: "adding a
 * routine, managing exercises, viewing history, editing profile, and
 * showing the workout summary" should each open a popup the user closes
 * when they're done). Renders as a fixed-position backdrop + centered panel.
 * [2026-07-30] Rendered via createPortal straight into document.body -- a
 * real bug caught during live testing, not assumed fine: when this was a
 * plain in-tree render, the Edit Hero popup (ProfileSwitcher.jsx, nested
 * inside a .comic-panel--entry row) was completely unclickable, because
 * .comic-panel--entry's clip-path (the corner-notch panel trick) makes
 * Chromium treat that ancestor as the containing block for position:fixed
 * descendants instead of the real viewport -- the backdrop's tint painted
 * correctly but well out of its intended stacking position, so clicks meant
 * for it landed on whatever normal page content sat at those coordinates
 * instead. Portaling to document.body sidesteps this for good: it can no
 * longer be affected by clip-path/transform/filter/contain on ANY ancestor,
 * regardless of which component ends up rendering a Modal in the future.
 * Clicking the backdrop or the corner close icon both call `onClose`;
 * clicking inside the panel itself does not (stopPropagation).
 *
 * [2026-08-18] `tabKey`, optional: the tab (see TabContext.jsx) this Modal
 * logically belongs to, e.g. "routines" for RoutinePicker's Add Routine
 * dialog. Now that inactive tabs stay mounted (hidden via CSS, not
 * unmounted -- see TabContext.jsx's comment), a Modal left open when the
 * user switches to a different tab would otherwise keep floating on top of
 * it, since this component portals straight to document.body regardless of
 * its parent's visibility. When `tabKey` is given, Modal watches the
 * active tab and calls its own `onClose` automatically the moment it
 * stops matching -- the exact same handler its close button/backdrop/
 * Escape already use, so this is just one more (automatic) way to trigger
 * the close a user could've triggered themselves. Modals that belong to
 * the persistent header (outside any tab, e.g. ProfileSwitcher's Edit Hero
 * dialog) simply omit `tabKey` and are unaffected by tab switches.
 */
const Modal = ({ title, onClose, tabKey, children }) => {
  const activeTab = useActiveTab();

  // Escape-to-close, matching the backdrop-click/corner-X affordances.
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    if (tabKey && activeTab !== tabKey) onClose();
  }, [tabKey, activeTab, onClose]);

  return createPortal(
    <div className="comic-modal-backdrop" onClick={onClose}>
      <div className="comic-panel comic-modal" onClick={(e) => e.stopPropagation()}>
        <div className="comic-modal__header">
          <h2 className="comic-title" style={{ fontSize: "22px", margin: 0 }}>{title}</h2>
          <IconButton icon="close" onClick={onClose} color="#232526" label="Close" />
        </div>
        <div className="comic-modal__body">{children}</div>
      </div>
    </div>,
    document.body
  );
};

export default Modal;
